import CTAButton from "components/CTA/Button";
import FormTitle from "components/Form/Title";
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useUserStore } from "store/User";

interface Option {
  value: string;
  title: string;
}

interface FirmBroker {
  id: string;
  fullName: string;
}

interface FilterCaseProps {
  refetch: (params: { search: string; status: string; broker: string }) => void;
  options?: Option[];
}

const FilterCase: React.FC<FilterCaseProps> = ({ refetch, options }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [broker, setBroker] = useState<string>("");

  const user = useUserStore((state) => state.user);

  // Initialize state from URL parameters on component mount
  useEffect(() => {
    const searchParam = searchParams.get("search") || "";
    const statusParam = searchParams.get("status") || "";
    const brokerParam = searchParams.get("broker") || "";

    setSearch(searchParam);
    setStatus(statusParam);
    setBroker(brokerParam);

    // If there are URL parameters, trigger the search immediately
    if (searchParam || statusParam || brokerParam) {
      refetch({
        search: searchParam,
        status: statusParam,
        broker: brokerParam,
      });
    }
  }, [searchParams, refetch]);

  // Update URL parameters when filters change
  const updateUrlParams = useCallback(
    (searchValue: string, statusValue: string, brokerValue: string) => {
      const params = new URLSearchParams();

      if (searchValue) params.set("search", searchValue);
      if (statusValue) params.set("status", statusValue);
      if (brokerValue) params.set("broker", brokerValue);

      setSearchParams(params);
    },
    [setSearchParams],
  );

  const handleSearch = () => {
    updateUrlParams(search, status, broker);
    refetch({ search, status, broker });
  };

  const handleReset = () => {
    setSearch("");
    setStatus("");
    setBroker("");
    setSearchParams({});
    refetch({
      search: "",
      status: "",
      broker: "",
    });
  };

  return (
    <div>
      <div className="mb-6 rounded-lg border border-gray-200 bg-white px-6 py-8 md:mb-0">
        <form className="md:-mt-4">
          <FormTitle mb left border title="Filter" />

          <div>
            <input
              type="text"
              name="search"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:outline-none sm:text-sm"
              placeholder="Search your query"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select
            className="mt-3 block w-full rounded-md border-gray-300 shadow-sm focus:outline-none sm:text-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option key="init" value="">
              -- Select Status --
            </option>
            {options &&
              options.map((opt, i) => (
                <option key={i} value={opt.value}>
                  {opt.title}
                </option>
              ))}
          </select>

          {user?.firmBrokers.length > 0 && (
            <select
              className="mt-3 block w-full rounded-md border-gray-300 shadow-sm focus:outline-none sm:text-sm"
              value={broker}
              onChange={(e) => setBroker(e.target.value)}
            >
              <option key="init" value="">
                -- Select Broker --
              </option>
              {user?.firmBrokers.map((broker: FirmBroker) => (
                <option key={broker.id} value={broker.id}>
                  {broker.fullName}
                </option>
              ))}
            </select>
          )}

          <div className="mt-4 flex flex-col space-y-4">
            <CTAButton secondary label="Reset" onClick={handleReset} />
            <CTAButton label="Filter" onClick={handleSearch} />
          </div>
        </form>
      </div>
    </div>
  );
};

FilterCase.displayName = "FilterCase";

export default FilterCase;
